import { useMemo, useRef, useState } from 'react'
import { parseDql, readQuerySpec, writeQuerySpec } from '@bloo-team/api-client'
import { BuilderPanel, QueryHighlight, useSearchFields, useStreams } from '@bloo-team/core-shell'
import type { BuilderSeed } from '@bloo-team/core-shell'
import type { BlockEditorProps, BlockViewProps } from './types'

/**
 * Search: a dql block authored in the visual builder (`block: "GUI"`). The
 * editor is core-shell's BuilderPanel, seeded through parseDql.
 *
 * Captured Search blocks carry the LEGACY builder text
 * (`_fetch * from event where $Stream=… AND $Duration=…`), which parseDql does
 * not read — BuilderPanel seeds only from `stream=…` form. Such a block edits
 * as text until someone starts it over in the builder.
 */

export function SearchView({ block }: BlockViewProps) {
  const { query } = readQuerySpec(block)
  return query ? <QueryHighlight className="wb-well" value={query} dialect="dql" /> : <p className="hud-unit">No search built yet.</p>
}

export function SearchEditor({ block, clusterId, scopeId, index, onChange, onRun }: BlockEditorProps) {
  const { query } = readQuerySpec(block)
  const vocab = useSearchFields({ clusterId, scopeId })
  const streamsInfo = useStreams({ clusterId, scopeId })
  const streamRank = useMemo(() => {
    const m = new Map<string, number>()
    for (const s of streamsInfo.data?.streams ?? []) m.set(s.stream, s.size_on_disk)
    return m
  }, [streamsInfo.data])
  const parsed = query.trim() ? parseDql(query) : null
  const [rebuild, setRebuild] = useState(false)
  /** BuilderPanel reports its compiled query on mount; that first report is
   * the stored query re-spelled, not an edit, so it must not dirty the draft. */
  const mounted = useRef(false)
  const legacy = query.trim() !== '' && parsed === null && !rebuild

  // Read ONCE per mount by BuilderPanel — the key below remounts it on rebuild.
  const seed: BuilderSeed | undefined = parsed
    ? {
        streams: parsed.streams,
        time: { kind: 'relative', duration: parsed.duration ?? '1h' },
        filters: parsed.filters,
        aggregation: parsed.aggregation,
        limit: parsed.limit,
      }
    : undefined

  if (legacy) {
    return (
      <>
        <div className="bui-alert bui-alert-info" role="note">
          <span className="bui-alert-mark">i</span>
          <span>This search was built in the earlier builder, which this editor cannot read — edit it as text, or start it over in the builder.</span>
        </div>
        <textarea
          className="hunt-editor mono"
          aria-label={`Block ${index + 1} search query`}
          value={query}
          rows={3}
          spellCheck={false}
          onChange={(e) => onChange(writeQuerySpec(block, { query: e.target.value }))}
        />
        <div>
          <button type="button" className="list-chip" onClick={() => setRebuild(true)}>
            start over in the builder
          </button>
        </div>
      </>
    )
  }

  return (
    <BuilderPanel
      key={rebuild ? 'rebuild' : 'seeded'}
      vocabulary={vocab.data}
      vocabularyPending={vocab.isPending}
      disabled={false}
      seed={seed}
      streamRank={streamRank}
      costLabel={null}
      onRun={() => onRun()}
      onDqlChange={(dql) => {
        if (!mounted.current && !rebuild) {
          mounted.current = true
          return
        }
        mounted.current = true
        if (dql !== query) onChange(writeQuerySpec(block, { query: dql }))
      }}
    />
  )
}
